/* global chrome */
import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "reactstrap";

const EXTENSION_ID = "eggbkdjkmoobojkldbkebmlkggafcnom";
const EXTENSION_CHECK_TIMEOUT = 1000; // ms timeout for extension check
const POLLING_INTERVAL = 5000; // ms polling interval
const MIN_SUPPORTED_VERSION = "1.2";

// semver-compare: returns 1 if a>b, 0 if a===b, -1 if a<b
function compareVersions(a = "0.0.0", b = "0.0.0") {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] || 0) > (pb[i] || 0)) return 1;
    if ((pa[i] || 0) < (pb[i] || 0)) return -1;
  }
  return 0;
}

function getBrowserName() {
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor;

  if (/Edg/.test(userAgent)) return "Edge";
  if (/OPR|Opera/.test(userAgent)) return "Opera";
  if (/Chrome/.test(userAgent) && vendor === "Google Inc.") return "Chrome";
  if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) return "Safari";
  if (/Firefox/.test(userAgent)) return "Firefox";
  return "Unknown";
}

// Helper function to check if a timestamp is from today
const isTimestampFromToday = (timestamp) => {
  if (!timestamp) return false;
  const genDate = new Date(timestamp);
  const today = new Date();
  return (
    genDate.getFullYear() === today.getFullYear() &&
    genDate.getMonth() === today.getMonth() &&
    genDate.getDate() === today.getDate()
  );
};

function WalmartInstructions({ onDataFetched }) {
  const [status, setStatus] = useState({
    isInstalled: false,
    isVersionCompatible: false,
    generationTimestamp: null,
  });
  const [browserName, setBrowserName] = useState("");

  // Check if the extension is installed and compatible
  const checkExtensionInstalled = () => {
    return new Promise((resolve) => {
      if (typeof chrome !== "undefined" && chrome.runtime) {
        const timeout = setTimeout(
          () => resolve({ installed: false }),
          EXTENSION_CHECK_TIMEOUT
        );

        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: "detectExtension" },
          (response) => {
            clearTimeout(timeout);
            if (response && response.installed) {
              const version = response.version || "0.0.0";
              const isCompatible = compareVersions(
                version,
                MIN_SUPPORTED_VERSION
              ) >= 0;
              resolve({ installed: true, version, isCompatible });
            } else {
              resolve({ installed: false, isCompatible: false });
            }
          }
        );
      } else {
        resolve({ installed: false, isCompatible: false });
      }
    });
  };

  // Check for the generation timestamp from the extension
  const checkGenerationTimestamp = () => {
    return new Promise((resolve) => {
      try {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: "getGenerationTimestamp" },
          (response) => {
            if (chrome.runtime.lastError || !response || !response.success) {
              resolve(null);
            } else {
              resolve(response.timestamp);
            }
          }
        );
      } catch (error) {
        resolve(null);
      }
    });
  };

  // Poll for the statuses
  const pollStatus = async () => {
    const extensionCheck = await checkExtensionInstalled().catch(() => ({
      installed: false,
      isCompatible: false
    }));
    const { installed: isInstalled, isCompatible } = extensionCheck;

    let generationTimestamp = null;
    if (isInstalled && isCompatible) {
      generationTimestamp = await checkGenerationTimestamp().catch(() => null);
    }

    const dataFresh = isTimestampFromToday(generationTimestamp);

    if (typeof onDataFetched === "function") {
      onDataFetched({ dataFresh, generationTimestamp });
    }

    setStatus({ isInstalled, isVersionCompatible: isCompatible, generationTimestamp });
  };

  // Initialize browser name and start polling
  useEffect(() => {
    setBrowserName(getBrowserName());
    pollStatus();
    const intervalId = setInterval(pollStatus, POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  // Determine message for Step 4 based on the status and fresh data check
  let step4Message = "";
  let step4Color = "#f56565"; // default reddish for action prompt

  if (!status.generationTimestamp) {
    step4Message =
      "Please follow the above steps to collect your orders before proceeding!";
  } else if (isTimestampFromToday(status.generationTimestamp)) {
    step4Message = "Orders fetched successfully!";
    step4Color = "#48bb78"; // green for success
  } else {
    step4Message = "Please collect the latest orders before proceeding!";
  }

  // Build the ordered instruction steps
  const getSteps = ({ isInstalled, isVersionCompatible }) => {
    const steps = [
      {
        description: (
          <>
            Before proceeding, please ensure you're using Chrome.
            {browserName && (
              <span style={{ fontWeight: "bold", marginLeft: 8 }}>
                {browserName === "Chrome" ? (
                  <span style={{ color: "#38A169" }}>(✅ You're using: Chrome)</span>
                ) : (
                  <span style={{ color: "#E53E3E" }}>(❌ You're using: {browserName})</span>
                )}
              </span>
            )}
          </>
        ),
      }
    ];

    if (!isInstalled) {
      steps.push({
        description: (
          <>Install the Measure Extension from the <a
            href="https://chromewebstore.google.com/detail/measure-order-exporter/eggbkdjkmoobojkldbkebmlkggafcnom"
            target="_blank"
            rel="noopener noreferrer"
          >Chrome Web Store</a>.</>
        ),
      });
    } else if (!isVersionCompatible) {
      steps.push({
        description: (
          <>Your Measure Extension is out of date. Please update it to the latest version via the <a
          href="https://chromewebstore.google.com/detail/measure-order-exporter/eggbkdjkmoobojkldbkebmlkggafcnom"
          target="_blank"
          rel="noopener noreferrer"
        >Chrome Web Store.</a></>
        ),
      });
    }

    steps.push(
      {
        description: (
          <>Go to Walmart.com, sign in, and navigate to your <a
            href="https://www.walmart.com/orders"
            target="_blank"
            rel="noopener noreferrer"
          >purchase history</a>.</>
        ),
      },
      {
        description:
          'Launch the Measure Extension and click "Start Collection" to begin collecting your orders. Return here once it is done.',
      },
      {
        description: (
          <p style={{ color: step4Color, fontWeight: "bold" }}>
            {step4Message}
          </p>
        ),
      }
    );

    return steps;
  };

  return (
    <Container fluid="sm" className="retro-instructions">
      <Row>
        <Col>
          <div className="instructions-summary">
            <h2>Before you start</h2>
            <p>
              It's important you complete each step, or we won't be able to
              process your payment! Our instructions are easy to follow.
            </p>
          </div>
          <div className="steps">
            {getSteps(status).map((step, index) => (
              <div key={index} className="step">
                <div className="step-number">{index + 1}</div>
                <div className="step-description">{step.description}</div>
              </div>
            ))}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default WalmartInstructions;
